import React, { useState } from 'react';
import DashboardLayout from '../../components/layouts/DashboardLayout';
import { dtcAPI } from '../../services/api';
import { Search, AlertCircle, Loader2, Info } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';

const SEVERITY_STYLES = {
  critical: 'bg-red-500/10 text-red-400 border-red-500/20',
  warning: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  info: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
};

const DTCSearchPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const isCode = /^[PBCUp]\d{4}/i.test(query.trim());
      const params = isCode ? { code: query.trim().toUpperCase() } : { keyword: query.trim() };
      const res = await dtcAPI.search(params);
      setResults(res.data || []);
    } catch (err) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto" data-testid="dtc-search-page">
        <div className="mb-8">
          <h1 className="font-heading text-2xl font-bold text-white">DTC-Code Suche</h1>
          <p className="text-slate-400 mt-1 text-sm">Suchen Sie nach Fehlercodes (P0xxx, B0xxx, C0xxx, U0xxx) oder Schlüsselwörtern</p>
        </div>

        <form onSubmit={handleSearch} className="flex gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="z.B. P0420, Katalysator, Lambda..."
              className="input-field pl-10"
              data-testid="dtc-search-input"
            />
          </div>
          <Button type="submit" disabled={loading} className="btn-primary" data-testid="dtc-search-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Suchen'}
          </Button>
        </form>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-[#EC2229] animate-spin" />
          </div>
        )}

        {!loading && searched && results.length === 0 && (
          <div className="glass-card p-8 text-center">
            <Info className="w-10 h-10 text-slate-500 mx-auto mb-3" />
            <p className="text-white font-medium mb-1">Keine Ergebnisse gefunden</p>
            <p className="text-sm text-slate-400">
              Versuchen Sie einen anderen Suchbegriff oder DTC-Code.
              Die Datenbank wird kontinuierlich erweitert.
            </p>
          </div>
        )}

        {!loading && results.length > 0 && (
          <div className="space-y-3" data-testid="dtc-results">
            <p className="text-sm text-slate-500 mb-4">{results.length} Ergebnis(se) gefunden</p>
            {results.map((dtc, i) => (
              <div key={i} className="glass-card p-5 hover:border-[#EC2229]/20 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono text-lg font-bold text-[#EC2229]">{dtc.code}</span>
                      {dtc.severity && (
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold border ${SEVERITY_STYLES[dtc.severity] || SEVERITY_STYLES.info}`}>
                          {dtc.severity === 'critical' ? 'Kritisch' : dtc.severity === 'warning' ? 'Warnung' : 'Info'}
                        </span>
                      )}
                      {dtc.system && (
                        <span className="badge-neutral text-xs">{dtc.system}</span>
                      )}
                    </div>
                    <p className="text-white text-sm">{dtc.description?.de || dtc.description?.en || 'Keine Beschreibung'}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!searched && (
          <div className="glass-card p-8 text-center">
            <Search className="w-10 h-10 text-slate-600 mx-auto mb-3" />
            <p className="text-white font-medium mb-1">DTC-Code Datenbank</p>
            <p className="text-sm text-slate-400">
              Geben Sie einen DTC-Code (z.B. P0420) oder ein Schlüsselwort ein, um die Datenbank zu durchsuchen.
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DTCSearchPage;
